import React, { useMemo } from 'react';
import styled from 'styled-components';

import Megaphone from '@/components/atom/icon/Megaphone';
import WarningCircle from '@/components/atom/icon/WarningCircle';
import useGetMyUncheckedAiAnswer from '@/hooks/qna/mobile/Home/useGetMyUncheckedAiAnswer';
import useCustomRouter from '@/hooks/useCustomRouter';
import { AiAnswerStatus, QuestionStatus } from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';

interface Props {
  containerStyle?: React.CSSProperties;
}
const AiNotificationBanner = ({ containerStyle }: Props) => {
  const aiAnswer = useGetMyUncheckedAiAnswer();
  const { moveToPage } = useCustomRouter();

  const bannerIcon = useMemo(
    () =>
      aiAnswer?.status === QuestionStatus.PaymentFailed ? (
        <WarningCircle width={16} height={16} />
      ) : aiAnswer?.aiAnswerStatus === AiAnswerStatus.GenerationComplete ? (
        <Megaphone />
      ) : (
        <WarningCircle width={16} height={16} />
      ),
    [aiAnswer?.aiAnswerStatus],
  );

  const bannerText = useMemo(
    () =>
      aiAnswer?.status === QuestionStatus.PaymentFailed
        ? '의료진 상담 결제가 실패됐어요.'
        : aiAnswer?.aiAnswerStatus === AiAnswerStatus.GenerationComplete
        ? '확인하지 않은 답변이 있어요'
        : '불러오지 못한 답변이 있어요',
    [aiAnswer?.aiAnswerStatus],
  );

  const buttonText = useMemo(
    () =>
      aiAnswer?.status === QuestionStatus.PaymentFailed
        ? '확인하기'
        : aiAnswer?.aiAnswerStatus === AiAnswerStatus.GenerationComplete
        ? '보러가기'
        : '확인하기',
    [aiAnswer?.aiAnswerStatus],
  );
  const onClickQuestionDetail = (questionPid: string) => {
    if (aiAnswer?.status === QuestionStatus.PaymentFailed) {
      moveToPage({
        webLink: `${ROUTES.QNA.DETAIL.path}/${questionPid}`,
        mobileScreenName: ROUTES.QNA.DETAIL.appScreenName,
        params: {
          questionId: aiAnswer.questionPid,
        },
      });
      return;
    }
    moveToPage({
      webLink: `${ROUTES.QNA.AI.path}/${questionPid}`,
      mobileScreenName: ROUTES.QNA.AI.appScreenName,
      params: {
        questionPid,
      },
    });
  };

  if (
    aiAnswer &&
    (aiAnswer.aiAnswerStatus === AiAnswerStatus.GenerationRequested ||
      aiAnswer.aiAnswerStatus === AiAnswerStatus.Generating ||
      aiAnswer.aiAnswerStatus === AiAnswerStatus.GenerationComplete ||
      aiAnswer.aiAnswerStatus === AiAnswerStatus.GenerationFailed)
  ) {
    return (
      <AiBanner style={{ ...containerStyle }}>
        <div>
          <div>
            {bannerIcon}
            {bannerText}
          </div>
          <button
            type="button"
            onClick={() => onClickQuestionDetail(aiAnswer?.questionPid ?? '')}
          >
            {buttonText}
          </button>
        </div>
      </AiBanner>
    );
  }

  return null;
};

export default AiNotificationBanner;

const AiBanner = styled.div`
  padding: 24px 20px 0;
  background-color: ${({ theme }) => theme.color.white};
  & > div {
    width: 100%;
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: ${({ theme }) => theme.color.gray40};
    border-radius: 16px;
    ${({ theme }) => theme.typography.UI16M}
    color: ${({ theme }) => theme.color.gray600};

    & > div {
      display: flex;
      align-items: center;
      svg {
        margin-right: 12px;
      }
    }

    & > button {
      padding: 10px 12px;
      display: flex;
      align-items: center;
      border-radius: 8px;
      ${({ theme }) => `
        ${theme.typography.UI14SB}
        background-color: ${theme.color.primary500}
        `};
      color: ${({ theme }) => theme.color.white};
    }
  }
`;
